// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * What a plan does to Board A, stated before it is done.
 *
 * The counts a diff produces answer "what is different". They are the wrong
 * numbers to show someone about to overwrite a board file. These answer the
 * question they are actually asking — *what will my board look like afterwards* —
 * in the vocabulary of the thing being changed: footprints, tracks, nets, zones.
 *
 * Two of these deserve to be read even when everything else is skimmed:
 *
 *  - `unselected`, because the dangerous mistake in a per-change merge is never
 *    what you took. It is what you forgot.
 *  - `zonesNeedingRefill`, because a stale zone fill is invisible on screen and
 *    arrives at the fabricator as copper that is not there.
 */

import type { DiffResult, ObjectChange } from '../diff/diff.ts';
import type { Board, BoardObject } from '../model/board.ts';
import { netNodes, netReference } from '../model/objects.ts';
import type { GlobalBlockId } from './plan.ts';
import type { MergePlan } from './plan.ts';

export interface TypeCounts {
  [type: string]: number;
}

export interface MergeEffects {
  /** In Board B and not Board A: will be created. */
  added: TypeCounts;
  /** In Board A and not Board B: will be deleted. */
  removed: TypeCounts;
  /** In both, differing: Board A's will be replaced by Board B's. */
  replaced: TypeCounts;
  /** Total objects touched, so the headline needs no arithmetic. */
  total: number;

  /** How many changes exist that no selection claims. */
  unselected: number;

  /** Net names referenced by anything being taken. */
  netsReferenced: string[];
  /**
   * Referenced nets Board A cannot supply, where nets are indexed through a
   * table. Taking these needs the table extending.
   */
  netsMissingInA: string[];
  /**
   * Nets that will come into existence, where nets are named inline (KiCad 10).
   * The object carries its own net name, so there is nothing to look up — but a
   * merge that quietly invents nets is worth saying out loud.
   */
  netsIntroduced: string[];
  /** Layers drawn on by anything being taken that Board A does not have. */
  layersMissingInA: string[];

  /** Zones whose fill cache will be discarded, so KiCad must refill them. */
  zonesNeedingRefill: number;

  /** Changes per layer, so a rectangle cannot quietly reach onto a hidden layer. */
  byLayer: TypeCounts;

  /** Board-level blocks being taken from Board B. */
  globals: GlobalBlockId[];
}

export function computeEffects(diff: DiffResult, plan: MergePlan): MergeEffects {
  const added: TypeCounts = {};
  const removed: TypeCounts = {};
  const replaced: TypeCounts = {};
  const byLayer: TypeCounts = {};
  const netsReferenced = new Set<string>();
  let zonesNeedingRefill = 0;
  let total = 0;

  for (const change of diff.substantive) {
    if (!plan.take.has(change.key)) continue;
    total++;

    const side = change.b ?? change.a!;
    const bucket = change.b === undefined ? removed : change.a === undefined ? added : replaced;
    bucket[side.type] = (bucket[side.type] ?? 0) + 1;

    for (const layer of side.layers) byLayer[layer] = (byLayer[layer] ?? 0) + 1;

    // Only what arrives from B carries net references and fill caches into A.
    if (change.b !== undefined) {
      for (const name of netsOf(change.b)) netsReferenced.add(name);
      if (change.b.type === 'zone') zonesNeedingRefill++;
    }
    // A zone leaving A does not need refilling, but its neighbours' fills are
    // stale either way — KiCad recomputes the lot, so count the outgoing too.
    if (change.b === undefined && change.a?.type === 'zone') zonesNeedingRefill++;
  }

  const unselectedList = diff.substantive.filter((c) => !plan.take.has(c.key));
  const unknownNets = [...netsReferenced].filter((n) => n !== '' && !diff.a.allNetNames().has(n)).sort();

  return {
    added,
    removed,
    replaced,
    total,
    unselected: unselectedList.length,
    netsReferenced: [...netsReferenced].sort(),
    netsMissingInA: unknownNets.filter(() => !diff.a.usesNamedNets),
    netsIntroduced: unknownNets.filter(() => diff.a.usesNamedNets),
    layersMissingInA: layersMissing(diff.a, diff, plan).sort(),
    zonesNeedingRefill,
    byLayer,
    globals: [...plan.takeGlobal],
  };
}

/** Net names an object references, resolved through its own board's table. */
function netsOf(o: BoardObject): string[] {
  const out: string[] = [];
  if (o.netName !== undefined && o.netName !== '') out.push(o.netName);
  for (const node of netNodes(o.node)) {
    const ref = netReference(node);
    if (ref.name !== undefined && ref.name !== '') out.push(ref.name);
  }
  return out;
}

function layersMissing(a: Board, diff: DiffResult, plan: MergePlan): string[] {
  // Board A's stack is what the output has, unless the stack itself is taken.
  if (plan.takeGlobal.has('layers')) return [];
  const have = new Set(a.layerTable.names);
  const missing = new Set<string>();
  for (const change of diff.substantive) {
    if (!plan.take.has(change.key) || change.b === undefined) continue;
    for (const layer of change.b.layers) {
      // Pseudo-layers are the renderer's, not the file's.
      if (!layer.startsWith('~') && !have.has(layer)) missing.add(layer);
    }
  }
  return [...missing];
}

/**
 * One-line summaries, in the order they should be read.
 *
 * Returned as data rather than a formatted string so the UI can style the
 * dangerous lines differently from the merely informative ones.
 */
export interface EffectLine {
  text: string;
  tone: 'neutral' | 'warn' | 'danger';
}

export function describeEffects(effects: MergeEffects, boardAName: string): EffectLine[] {
  const lines: EffectLine[] = [];
  const counts = (c: TypeCounts): string =>
    Object.entries(c)
      .sort(([, x], [, y]) => y - x)
      .map(([type, n]) => `${n} ${type}${n === 1 ? '' : 's'}`)
      .join(', ');

  if (effects.total === 0) {
    lines.push({ text: `Nothing selected — ${boardAName} would be unchanged.`, tone: 'neutral' });
  } else {
    if (Object.keys(effects.added).length > 0) {
      lines.push({ text: `Add ${counts(effects.added)}.`, tone: 'neutral' });
    }
    if (Object.keys(effects.removed).length > 0) {
      lines.push({ text: `Delete ${counts(effects.removed)}.`, tone: 'warn' });
    }
    if (Object.keys(effects.replaced).length > 0) {
      lines.push({ text: `Replace ${counts(effects.replaced)}.`, tone: 'neutral' });
    }
  }

  if (effects.unselected > 0) {
    lines.push({
      text: `${effects.unselected} difference(s) are in no selection and will not be merged.`,
      tone: 'warn',
    });
  }

  if (effects.netsIntroduced.length > 0) {
    lines.push({
      text:
        `${effects.netsIntroduced.length} new net(s) will be created: ` +
        `${effects.netsIntroduced.slice(0, 4).join(', ')}${effects.netsIntroduced.length > 4 ? '…' : ''}.`,
      tone: 'warn',
    });
  }
  if (effects.netsMissingInA.length > 0) {
    lines.push({
      text:
        `${effects.netsMissingInA.length} net(s) used here do not exist in ${boardAName}: ` +
        `${effects.netsMissingInA.slice(0, 4).join(', ')}${effects.netsMissingInA.length > 4 ? '…' : ''}.`,
      tone: 'danger',
    });
  }
  if (effects.layersMissingInA.length > 0) {
    lines.push({
      text: `${effects.layersMissingInA.join(', ')} do(es) not exist in ${boardAName}.`,
      tone: 'danger',
    });
  }
  if (effects.zonesNeedingRefill > 0) {
    lines.push({
      text: `${effects.zonesNeedingRefill} zone(s) must be re-filled in KiCad before fabrication.`,
      tone: 'warn',
    });
  }
  for (const g of effects.globals) {
    lines.push({ text: `Take Board B's ${g}, replacing Board A's outright.`, tone: 'danger' });
  }

  return lines;
}

/** Changes claimed by nothing, for the "what did I forget" list. */
export function unselectedChanges(diff: DiffResult, plan: MergePlan): ObjectChange[] {
  return diff.substantive.filter((c) => !plan.take.has(c.key));
}
